// src/contexts/UpgradeModalContext.js
//
// Global state for the "Show but Lock" upgrade modal.
// Free / lower-tier users see paid features but interaction opens this single
// upgrade modal. Pattern matches SubscriptionContext.js for consistency.
//
// Usage:
//   const { open, close, isOpen, modalProps } = useUpgradeModal();
//   open({
//     feature: 'aiInsights',
//     requiredPlan: 'premium',
//     promoText: 'AI duplicate detection',
//     currentPlan: 'free',
//   });
//
// Cooldown: at most 1 *auto-triggered* open per browser session. Explicit
// user clicks always bypass the cooldown by passing { source: 'user' } (default).
// Auto-triggers (e.g. from API 403 events) should pass { source: 'auto' }.
//
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

const UpgradeModalContext = createContext(null);

const SESSION_KEY = 'upgradeModal:autoOpenedThisSession';
const UPGRADE_EVENT = 'upgrade-required';

export const useUpgradeModal = () => {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    // Fail-soft: if provider is missing, return a no-op surface so consumers
    // don't crash. Apollo wires the provider in a separate sprint.
    return {
      open: () => false,
      close: () => {},
      isOpen: false,
      modalProps: null,
    };
  }
  return ctx;
};

export const UpgradeModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * open(props)
   *   props = {
   *     feature, requiredPlan, promoText, currentPlan,
   *     source: 'user' | 'auto' (default 'user'),
   *     message?: string
   *   }
   * Returns true if the modal was opened, false if suppressed by cooldown.
   */
  const open = useCallback((props = {}) => {
    const source = props.source || 'user';

    if (source === 'auto') {
      try {
        const already = sessionStorage.getItem(SESSION_KEY);
        if (already === '1') {
          return false;
        }
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch (e) {
        // sessionStorage may be unavailable (private mode); allow open.
      }
    }

    setModalProps({
      feature: props.feature || null,
      requiredPlan: props.requiredPlan || 'premium',
      promoText: props.promoText || '',
      currentPlan: props.currentPlan || null,
      message: props.message || null,
      source,
    });
    setIsOpen(true);
    return true;
  }, []);

  // Listen for `upgrade-required` events dispatched by the axios interceptor
  // (see services/api.js). These are auto-opens and respect the session
  // cooldown.
  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      open({
        feature: detail.feature,
        requiredPlan: detail.requiredPlan,
        message: detail.message,
        promoText: detail.promoText || detail.feature || '',
        source: 'auto',
      });
    };
    window.addEventListener(UPGRADE_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_EVENT, handler);
  }, [open]);

  const value = useMemo(
    () => ({ open, close, isOpen, modalProps }),
    [open, close, isOpen, modalProps]
  );

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
    </UpgradeModalContext.Provider>
  );
};

export default UpgradeModalContext;
